import { useState, type ReactNode } from 'react';
import { formatMergeMoney } from '@/lib/invoice-merge-guidance';
import { changedAfterSendLine } from '@/lib/sendEvidence';
import { describeDescriptionChange, descriptionChangeIsShortened } from '@/lib/descriptionChangeText';
import type {
  InvoiceDocumentHistoryData,
  InvoiceIdentityFieldName,
  LineageHistoryEvent,
} from '@/lib/invoiceSelectiveHistoryContracts';

export interface InvoiceDocumentHistoryProps {
  data: InvoiceDocumentHistoryData | null;
  invoiceStatus?: string | null;
  /**
   * `originalInvoiceId` is passed only for a saved copy that belongs to an invoice other than the
   * one on screen (an invoice merged into this one). The archive is always opened under the
   * revision's own invoice so its original bytes and hash are what come back.
   */
  onDownloadRevision(revisionId: string, originalInvoiceId?: string, filenameSuffix?: string): void;
  onAssertExternalSend(revisionId: string | null): void;
  busy?: boolean;
  loading?: boolean;
  error?: string | null;
  /** SPEC-invoice-merge-history R3: source-labelled history of the whole merged lineage. */
  lineageEvents?: LineageHistoryEvent[];
  lineageHasMore?: boolean;
  lineageLoading?: boolean;
  lineageError?: string | null;
  onLoadMoreLineage?(): void;
  /** Opens another invoice (the one an old number was merged into). */
  onOpenInvoice?(invoiceId: string): void;
  /** Saved copies whose stored bytes turned out to be unavailable (the archive says so honestly). */
  unavailableRevisionIds?: ReadonlySet<string>;
}

const fieldLabels: Record<InvoiceIdentityFieldName, string> = {
  legal_name: 'Legal name',
  display_name: 'Client display name',
  dba: 'DBA',
  billing_address: 'Billing address',
};

function humanize(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/** Plain names for recorded change intents whose code name reads badly when humanized. */
const intentLabels: Record<string, string> = {
  add_dba: 'Added a DBA',
};

function intentLabel(intent: string): string {
  return intentLabels[intent] ?? humanize(intent);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Time not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined) return 'Not set';
  if (value === '') return 'Blank';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function money(value: unknown): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : displayValue(value);
}

function sourceLabel(source: string): string {
  const known: Record<string, string> = {
    invoice_override: 'invoice-specific value',
    explicit_override: 'invoice-specific value',
    establishment: 'establishment',
    organization: 'client record',
    project: 'project',
    custom: 'custom value',
    hidden: 'hidden DBA setting',
    organization_name_fallback: 'client name fallback',
    inherited: 'its current source',
    unresolved: 'unresolved source',
  };
  return known[source] ?? humanize(source).toLowerCase();
}

function eventDescription(kind: string, details: Record<string, unknown> | null, evidenceClass?: string): string {
  switch (kind) {
    case 'prepared':
      return 'PDF prepared. This does not record a download or a send.';
    case 'export_requested':
      return 'PDF download requested. This does not confirm that the file was saved or sent.';
    case 'internal_send_prepared':
      return 'PDF prepared for a Keystone email. This is not confirmation of delivery.';
    case 'internal_send_accepted':
      return details?.resolved_by_person === true
        ? 'A staff member resolved an uncertain Keystone send as sent.'
        : 'Keystone’s email provider accepted the send request.';
    case 'internal_send_resolved_sent':
      return 'A staff member marked an uncertain Keystone send as sent.';
    case 'internal_send_failed':
      return 'The Keystone email send failed.';
    case 'internal_send_unknown':
      return 'The Keystone email outcome was recorded as unknown at this time.';
    case 'manual_external_send_asserted':
      if (evidenceClass === 'forwarded_evidence' || details?.evidence_origin === 'forwarded_message') {
        return 'A person recorded this saved PDF as evidence that was forwarded to them. It was not sent by Keystone and does not confirm delivery.';
      }
      return details?.document_evidence === 'explicit_revision'
        ? 'A person recorded that it was sent outside Keystone and selected this saved PDF.'
        : 'A person recorded that it was sent outside Keystone; the PDF copy is unknown.';
    default:
      return `Recorded event: ${humanize(kind)}.`;
  }
}

const revisionOriginLabels: Record<string, string> = {
  provider_attachment: 'Attached to a Keystone email (provider acceptance does not confirm delivery)',
  forwarded_evidence: 'Forwarded evidence recorded by a person, not a Keystone email',
  person_asserted_external_send: 'Recorded by a person as sent outside Keystone',
  prepared_or_downloaded: 'Saved when the PDF was prepared or downloaded',
};

/**
 * The row's place in a merged lineage (CHR-R5-01, -10, -11). Every label comes from the SERVER, computed from the saved copy's
 * own recorded facts and its position against every merge and edit; this screen never derives "current" or a total itself.
 */
interface VersionInfo {
  version_role?: string;
  before_combining?: boolean;
  version_label?: string;
  version_tag?: string;
  version_note?: string | null;
  is_current_version?: boolean;
  invoice_total?: string | null;
  total_source?: string | null;
  later_merge_text?: string | null;
  /** How the invoice compares with this copy now: unchanged | amounts_only | content_changed | unknown (own copies only). */
  since_saved?: string | null;
  download_suffix?: string;
}

const isOriginalVersion = (row: VersionInfo): boolean => row.version_role === 'original_before_combining';
const isEarlierVersion = (row: VersionInfo): boolean => row.version_role === 'combined_earlier_version';
const isCombinedVersion = (row: VersionInfo): boolean => row.version_role === 'combined_version';
const hasVersion = (row: VersionInfo): boolean => isOriginalVersion(row) || isEarlierVersion(row) || isCombinedVersion(row);

/** Short, unmistakable tag used wherever a saved copy or send is listed or chosen. */
function versionTag(row: VersionInfo): string {
  if (row.version_tag) return row.version_tag;
  if (isOriginalVersion(row)) return 'ORIGINAL, before combining';
  if (isEarlierVersion(row)) return 'COMBINED, earlier version';
  if (isCombinedVersion(row)) return row.is_current_version === true ? 'COMBINED, current version' : 'COMBINED';
  return '';
}

/** A distinct download name per saved copy (role, save time and copy id), so two copies can never be mistaken for each other. */
function downloadSuffix(row: VersionInfo, copyId: string): string | undefined {
  if (row.download_suffix) return row.download_suffix;
  if (!hasVersion(row)) return undefined;
  const role = isOriginalVersion(row)
    ? 'ORIGINAL (before combining)'
    : isEarlierVersion(row)
      ? 'COMBINED (earlier version)'
      : 'COMBINED (current version)';
  return `${role} copy ${copyId.slice(0, 8)}`;
}

/** "Total on this copy" only when the copy recorded its own total; never a figure borrowed from a merge record. */
function copyTotalText(row: VersionInfo): string {
  return row.invoice_total ? `Total on this copy ${formatMergeMoney(row.invoice_total)}` : 'Total on this copy was not recorded';
}

/** A send recorded against a copy is a send of THAT copy: say which one, with its own total and what came after it. */
function sentCopyText(row: VersionInfo): string | null {
  const total = row.invoice_total ? ` (the ${formatMergeMoney(row.invoice_total)} copy)` : '';
  if (isOriginalVersion(row)) return `Sent before combining${total}. This is not a send of the combined invoice.`;
  if (isEarlierVersion(row)) {
    if (!row.later_merge_text && row.since_saved === 'amounts_only') {
      return `Sent as a combined copy${total}. Since then only amounts shown on the PDF (for example a payment received) changed; the invoice's content and totals are the same, so this send still stands.`;
    }
    const when = row.later_merge_text ? `, before ${row.later_merge_text}` : ', before the invoice changed';
    return `Sent as an earlier combined copy${total}${when}. This is not a send of the current version.`;
  }
  return null;
}

const SEND_EVIDENCE_KINDS = new Set(['manual_external_send_asserted', 'internal_send_accepted', 'internal_send_resolved_sent']);

const currentVersionText: Record<string, string> = {
  sent: 'A send is recorded for this version.',
  not_yet_sent: 'This version has not been sent yet.',
  send_outcome_unknown: 'A send of this version has an unconfirmed outcome. Resolve it before sending again.',
  sent_content_unverified: 'A send is recorded, but what the invoice said at that time was not recorded. This screen cannot confirm the current version is what was sent.',
};

const sendEvidenceText: Record<string, string> = {
  operator_assertion: 'You recorded this send without choosing a saved PDF, so the attachment itself is not proven. It is compared with the invoice as it stood when you recorded it.',
  archived_copy: 'The send is tied to a saved PDF whose contents are stored.',
};

function identityChangeLines(change: InvoiceDocumentHistoryData['billing_changes'][number]): ReactNode[] {
  const fields = change.material_fields ?? {};
  const lines: ReactNode[] = [];
  for (const field of Object.keys(fieldLabels) as InvoiceIdentityFieldName[]) {
    const material = fields[field];
    const before = change.before_fields[field];
    const after = change.after_fields[field];
    const changed = material ?? (before !== after ? { before, after } : undefined);
    if (changed) {
      lines.push(
        <li key={field}>
          <strong>{fieldLabels[field]}:</strong>{' '}
          <span>Before: {displayValue(changed.before)}</span>{' → '}
          <span>After: {displayValue(changed.after)}</span>
        </li>,
      );
    } else if (change.changed_fields.includes(field)) {
      lines.push(
        <li key={`source:${field}`}>
          <strong>{fieldLabels[field]}:</strong> source updated; printed value remains {displayValue(after)}.
        </li>,
      );
    }
  }

  const preserved = change.changed_fields
    .filter((field) => field.startsWith('preserved:'))
    .map((field) => field.slice('preserved:'.length));
  for (const field of preserved) {
    const label = field in fieldLabels ? fieldLabels[field as InvoiceIdentityFieldName] : humanize(field);
    lines.push(<li key={`preserved:${field}`}>Kept {label} as it was during this billing edit.</li>);
  }
  return lines;
}

/** Old and new line wording in full, behind a button so it also works on touch screens. */
function FullDescriptionWording({ before, after }: { before: unknown; after: unknown }) {
  const [open, setOpen] = useState(false);
  if (!descriptionChangeIsShortened(before, after)) return null;
  const shown = (value: unknown) => (typeof value === 'string' ? (value === '' ? '(blank)' : value) : '(not recorded)');
  return (
    <div className="mt-1 text-sm">
      <button
        type="button"
        className="text-sm underline underline-offset-2 hover:no-underline"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="description-change-toggle"
      >
        {open ? 'Hide full wording' : 'Show full wording'}
      </button>
      {open ? (
        <dl className="mt-2 space-y-2" data-testid="description-change-full">
          <div>
            <dt className="font-medium">Before</dt>
            <dd className="whitespace-pre-wrap break-words">{shown(before)}</dd>
          </div>
          <div>
            <dt className="font-medium">After</dt>
            <dd className="whitespace-pre-wrap break-words">{shown(after)}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}

function lineDescriptionOf(fields: unknown): unknown {
  return (fields as Record<string, unknown> | null | undefined)?.line_description;
}

function describeLineageEvent(event: LineageHistoryEvent, numberOf: (invoiceId: string | null | undefined) => string): string {
  const details = event.details as Record<string, unknown>;
  switch (event.kind) {
    case 'merge_decision':
      return details.role === 'survivor'
        ? 'Merge decision: this invoice kept its number and received the other invoice’s lines and payments.'
        : 'Merge decision: this invoice was merged into another invoice and cancelled; its lines and payments moved.';
    case 'identity_change':
      return `Billing change: ${intentLabel(String(details.intent ?? 'change'))}.`;
    case 'description_change':
      return describeDescriptionChange(lineDescriptionOf(details.before_fields), lineDescriptionOf(details.after_fields));
    case 'unsupported_change':
      return 'A change of a type this screen cannot display was recorded. It is kept but not interpreted.';
    case 'payment_received': {
      const how = [details.method, details.reference].filter(Boolean).join(' · ');
      const moved = details.moved_by_merge === true
        ? ` It now sits on invoice ${numberOf(details.now_on_invoice_id as string)}; the merge moved it and did not receive it again.`
        : '';
      return `Payment of ${money(details.amount)} recorded${how ? ` (${how})` : ''}.${moved}`;
    }
    case 'payment_voided':
      return `Payment of ${money(details.amount)} voided${details.void_reason ? `: ${String(details.void_reason)}` : ''}.`;
    case 'row_audit': {
      const columns = Array.isArray(details.changed_columns) ? (details.changed_columns as string[]) : [];
      return `Row change (${String(details.entity_type)} ${String(details.action).toLowerCase()})${columns.length ? `: ${columns.join(', ')}` : ''}.`;
    }
    case 'send_attempt': {
      const envelope = details.envelope as { to_list?: string[] } | null | undefined;
      const to = envelope?.to_list?.join(', ');
      const claim = details.delivery_claim === 'provider_accepted_not_delivery'
        ? ' The provider accepted the request; delivery is not confirmed.'
        : details.delivery_claim === 'staff_asserted'
          ? ' A person confirmed it was sent.'
          : details.delivery_claim === 'unknown'
            ? ' The outcome is not confirmed.'
            : '';
      const noCopy = details.document_availability === 'no_archived_copy' ? ' No saved PDF copy exists for this send.' : '';
      return `Keystone email attempt ${humanize(String(details.state)).toLowerCase()}${to ? ` to ${to}` : ''}.${claim}${noCopy}`;
    }
    default:
      return eventDescription(event.kind, details, event.evidence_class);
  }
}

export function InvoiceDocumentHistory({
  data,
  invoiceStatus,
  onDownloadRevision,
  onAssertExternalSend,
  busy = false,
  loading = false,
  error = null,
  lineageEvents,
  lineageHasMore = false,
  lineageLoading = false,
  lineageError = null,
  onLoadMoreLineage,
  onOpenInvoice,
  unavailableRevisionIds,
}: InvoiceDocumentHistoryProps) {
  const [selectedRevisionId, setSelectedRevisionId] = useState('');
  const needsSentTransition = invoiceStatus === 'draft' || invoiceStatus === 'ready';
  const identityChanges = data?.identity_change_events ?? data?.billing_changes ?? [];
  const revisionsById = new Map((data?.revisions ?? []).map((revision) => [revision.id, revision]));
  const lineage = data?.lineage;
  const requestedNumber = lineage?.members.find((member) => member.is_requested)?.invoice_number ?? null;
  const numberOf = (invoiceId: string | null | undefined): string =>
    lineage?.members.find((member) => member.invoice_id === invoiceId)?.invoice_number ?? 'another invoice';
  const currentVersion = data?.current_version;
  // Point at a COMBINED copy only when one is actually labelled current (after an edit or a merge none is, until the PDF is downloaded again).
  const sendCurrentAdvice = data?.revisions?.some((row) => row.is_current_version === true)
    ? 'To send the current invoice, download the current COMBINED copy instead.'
    : 'No saved copy is the current invoice yet. Use Download PDF at the top to get one.';

  return (
    <section aria-labelledby="invoice-document-history-title" className="space-y-5" data-testid="invoice-document-history">
      <header>
        <h2 id="invoice-document-history-title" className="text-lg font-semibold">Saved PDFs and billing history</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Saved PDFs stay available here. The current PDF is available separately and reflects current invoice details.
        </p>
      </header>

      {loading ? <p role="status">Loading invoice history…</p> : null}
      {error ? <p role="alert">Could not refresh invoice history: {error}</p> : null}

      {data ? (
        <>
          {lineage?.is_merged_away ? (
            <div role="status" className="rounded border border-blue-300 bg-blue-50 p-3 text-sm" data-testid="merged-notice">
              <p>
                Invoice {requestedNumber ?? 'this invoice'} was merged into invoice {lineage.current_invoice_number ?? 'another invoice'}.
                Its original history and saved PDFs stay available here.
              </p>
              {onOpenInvoice ? (
                <button
                  type="button"
                  className="mt-2 rounded border px-3 py-1.5 hover:bg-muted"
                  onClick={() => onOpenInvoice(lineage.current_invoice_id)}
                  data-testid="open-current-invoice"
                >
                  Open current invoice {lineage.current_invoice_number ?? ''}
                </button>
              ) : null}
            </div>
          ) : null}

          {currentVersion ? (
            <section aria-labelledby="current-version-title" className="space-y-1 rounded border p-3" data-testid="current-version-state">
              <h3 id="current-version-title" className="font-medium">Current version</h3>
              <p className="text-sm" data-testid="current-version-text">
                {currentVersion.state === 'not_yet_sent' && currentVersion.reason_code === 'changed_since_send'
                  ? changedAfterSendLine(invoiceStatus)
                  : currentVersionText[currentVersion.state] ?? `Send state: ${humanize(currentVersion.state)}.`}
              </p>
              {currentVersion.reason ? (
                <p className="text-sm text-muted-foreground">{currentVersion.reason.charAt(0).toUpperCase() + currentVersion.reason.slice(1)}.</p>
              ) : null}
              {currentVersion.send_evidence && sendEvidenceText[currentVersion.send_evidence] && currentVersion.state === 'sent' ? (
                <p className="text-sm text-muted-foreground" data-testid="send-evidence-note">{sendEvidenceText[currentVersion.send_evidence]}</p>
              ) : null}
              {(currentVersion.historical_send_count ?? 0) > 0 && currentVersion.has_merge_history ? (
                <p className="text-sm text-muted-foreground" data-testid="historical-sends-note">
                  {currentVersion.historical_send_count} send record{currentVersion.historical_send_count === 1 ? '' : 's'}{' '}
                  {currentVersion.historical_send_count === 1 ? 'was' : 'were'} made before these invoices were combined (on an original invoice, or on this one before it combined). {currentVersion.historical_send_count === 1 ? 'It stays' : 'They stay'} in history and {currentVersion.historical_send_count === 1 ? 'is' : 'are'} not a send of this version.
                </p>
              ) : null}
              {lineage?.has_merge_history ? (
                <p className="text-sm" data-testid="combined-current-note">
                  The combined invoice {lineage.current_invoice_number ?? ''} is the current version. Saved copies and sends marked
                  ORIGINAL are the documents as they were before combining. They are kept, but they are not the current invoice.
                </p>
              ) : null}
            </section>
          ) : null}

          <section aria-labelledby="saved-pdfs-title" className="space-y-2">
            <h3 id="saved-pdfs-title" className="font-medium">Saved PDF copies</h3>
            {data.revisions.length ? (
              <ul className="space-y-2">
                {data.revisions.map((revision) => {
                  const unavailable = unavailableRevisionIds?.has(revision.id) ?? false;
                  const original = revision.is_original_invoice === true && Boolean(revision.invoice_id);
                  const versioned = hasVersion(revision);
                  const suffix = downloadSuffix(revision, revision.id);
                  return (
                    <li
                      key={revision.id}
                      className={`flex flex-wrap items-center justify-between gap-3 rounded border p-3 ${
                        isEarlierVersion(revision)
                          ? 'border-slate-400 bg-slate-50'
                          : isCombinedVersion(revision)
                            ? 'border-green-600 bg-green-50'
                            : isOriginalVersion(revision)
                              ? 'border-amber-500 bg-amber-50'
                              : ''
                      }`}
                      data-testid={versioned ? `revision-row-${revision.version_role}` : undefined}
                    >
                      <div>
                        <p className="font-medium">
                          {versioned ? (
                            <span
                              className={`mr-2 rounded px-1.5 py-0.5 text-xs font-semibold uppercase ${
                                isEarlierVersion(revision)
                                  ? 'bg-slate-600 text-white'
                                  : isCombinedVersion(revision)
                                    ? 'bg-green-700 text-white'
                                    : 'bg-amber-600 text-white'
                              }`}
                              data-testid={`revision-badge-${revision.id}`}
                            >
                              {versionTag(revision)}
                            </span>
                          ) : null}
                          {revision.filename}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Saved {formatDate(revision.created_at)} by {revision.created_by} · {revision.byte_size} bytes · Copy {revision.id.slice(0, 8)}
                        </p>
                        {original || versioned ? (
                          <p
                            className="text-sm text-muted-foreground"
                            data-testid={original ? `revision-original-${revision.id}` : `revision-version-${revision.id}`}
                          >
                            {revision.version_label ?? `Original invoice ${revision.invoice_number ?? revision.invoice_id}`}
                            {versioned ? ` · ${copyTotalText(revision)}` : ''}
                          </p>
                        ) : null}
                        {isOriginalVersion(revision) || isEarlierVersion(revision) ? (
                          <p className="text-sm font-medium" data-testid={`revision-superseded-${revision.id}`}>
                            {isOriginalVersion(revision)
                              ? `This is the copy as it was before combining. ${sendCurrentAdvice}`
                              : `${revision.version_note ?? 'This copy has been superseded.'} ${revision.since_saved === 'amounts_only' ? '' : sendCurrentAdvice}`}
                          </p>
                        ) : null}
                        {revision.evidence_origin ? (
                          <p className="text-sm text-muted-foreground" data-testid={`revision-origin-${revision.id}`}>
                            {revisionOriginLabels[revision.evidence_origin] ?? humanize(revision.evidence_origin)}
                          </p>
                        ) : null}
                        {unavailable ? (
                          <p role="alert" className="text-sm text-amber-700" data-testid={`revision-unavailable-${revision.id}`}>
                            No stored copy of this saved PDF is available. The record is kept, and nothing is rebuilt from today’s invoice.
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                        onClick={() => {
                          if (suffix) onDownloadRevision(revision.id, original ? revision.invoice_id : undefined, suffix);
                          else if (original) onDownloadRevision(revision.id, revision.invoice_id);
                          else onDownloadRevision(revision.id);
                        }}
                        disabled={busy || unavailable}
                        data-testid={`download-revision-${revision.id}`}
                      >
                        {isOriginalVersion(revision)
                          ? 'Download ORIGINAL saved PDF'
                          : isEarlierVersion(revision)
                            ? 'Download EARLIER COMBINED saved PDF'
                            : isCombinedVersion(revision)
                              ? 'Download CURRENT COMBINED saved PDF'
                              : 'Download saved PDF'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : <p className="text-sm text-muted-foreground">No saved PDF copies are recorded.</p>}
          </section>

          <section aria-labelledby="external-send-title" className="space-y-2 rounded border p-3">
            <h3 id="external-send-title" className="font-medium">
              {needsSentTransition ? 'Mark as Sent outside Keystone' : 'Record an additional external send'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {needsSentTransition
                ? 'Use this after sending outside Keystone. This marks the invoice Sent and records your assertion together; it does not send email.'
                : 'Use this after another send outside Keystone. This records your assertion without changing the invoice status; it does not send email.'}
            </p>
            <label className="block text-sm" htmlFor="external-send-revision">PDF copy, if known</label>
            <select
              id="external-send-revision"
              value={selectedRevisionId}
              onChange={(event) => setSelectedRevisionId(event.currentTarget.value)}
              disabled={busy}
              className="block w-full max-w-xl rounded border bg-background px-3 py-2 text-sm"
              data-testid="external-send-revision-select"
            >
              <option value="">Unknown / not recorded</option>
              {data.revisions.map((revision) => (
                <option key={revision.id} value={revision.id}>
                  {hasVersion(revision) ? `[${versionTag(revision)}${revision.invoice_total ? ` ${formatMergeMoney(revision.invoice_total)}` : ''}] ` : ''}{revision.filename} · {formatDate(revision.created_at)} · {revision.id.slice(0, 8)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              onClick={() => onAssertExternalSend(selectedRevisionId || null)}
              disabled={busy}
              data-testid="assert-external-send"
            >
              {needsSentTransition
                ? (selectedRevisionId ? 'Mark as Sent with selected PDF' : 'Mark as Sent (copy unknown)')
                : (selectedRevisionId ? 'Record additional send with selected PDF' : 'Record additional send (copy unknown)')}
            </button>
          </section>

          <section aria-labelledby="document-events-title" className="space-y-2">
            <h3 id="document-events-title" className="font-medium">Document and send events</h3>
            {data.events.length ? (
              <ol className="space-y-2">
                {data.events.map((event) => {
                  const revision = event.revision_id ? revisionsById.get(event.revision_id) : undefined;
                  return (
                    <li key={event.id} className="rounded border p-3" data-testid="history-event">
                      <p className="font-medium">{eventDescription(event.kind, event.details, event.evidence_class)}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(event.occurred_at)} · {event.actor}
                        {revision ? ` · ${revision.filename}` : ''}
                        {hasVersion(event)
                          ? ` · ${event.version_label}`
                          : event.is_original_invoice ? ` · Original invoice ${event.invoice_number ?? ''}` : ''}
                      </p>
                      {(isOriginalVersion(event) || isEarlierVersion(event)) && SEND_EVIDENCE_KINDS.has(event.kind) ? (
                        <p
                          className="text-sm font-medium"
                          data-testid={isOriginalVersion(event) ? 'history-event-sent-before-combining' : 'history-event-sent-earlier-version'}
                        >
                          {sentCopyText(event)}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            ) : <p className="text-sm text-muted-foreground">No document or send events are recorded.</p>}
          </section>

          <section aria-labelledby="send-attempts-title" className="space-y-2">
            <h3 id="send-attempts-title" className="font-medium">Keystone email attempts</h3>
            {data.send_attempts.length ? (
              <ul className="space-y-2">
                {data.send_attempts.map((attempt) => {
                  const accepted = data.events.some((event) => event.send_attempt_id === attempt.id
                    && event.kind === 'internal_send_accepted' && event.details?.resolved_by_person !== true);
                  const resolvedSent = data.events.some((event) => event.send_attempt_id === attempt.id
                    && (event.kind === 'internal_send_resolved_sent' || event.details?.resolved_by_person === true));
                  let label = `Email attempt ${humanize(attempt.state)}`;
                  if (accepted) label = 'Email provider accepted the send request';
                  else if (attempt.state === 'SENT' && (resolvedSent || attempt.resolved_by)) {
                    label = 'Staff marked an uncertain email attempt as sent';
                  } else if (attempt.state === 'FAILED' && attempt.resolved_by) {
                    label = 'Staff marked an uncertain email attempt as not sent';
                  } else if (attempt.state === 'FAILED') label = 'The Keystone email send failed.';
                  else if (attempt.state === 'SENT') label = 'Email attempt is recorded as sent';
                  else if (attempt.state === 'UNKNOWN') label = 'Email attempt outcome is unknown';
                  return (
                    <li key={attempt.id} className="rounded border p-3">
                      <p className="font-medium">{label}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(attempt.completed_at ?? attempt.created_at)} · {attempt.sent_by}
                        {attempt.resolved_by ? ` · resolved by ${attempt.resolved_by}` : ''}
                        {attempt.document_evidence === 'explicit_revision'
                          ? ' · a PDF copy is linked'
                          : attempt.document_availability === 'no_archived_copy'
                            ? ' · PDF copy unknown (no saved copy exists for this send)'
                            : ' · PDF copy unknown'}
                        {hasVersion(attempt)
                          ? ` · ${attempt.version_label}${attempt.invoice_total ? ` · ${formatMergeMoney(attempt.invoice_total)}` : ''}`
                          : attempt.is_original_invoice ? ` · Original invoice ${attempt.invoice_number ?? ''}` : ''}
                      </p>
                      {isOriginalVersion(attempt) || isEarlierVersion(attempt) ? (
                        <p
                          className="text-sm font-medium"
                          data-testid={isOriginalVersion(attempt) ? 'send-attempt-before-combining' : 'send-attempt-earlier-version'}
                        >
                          {sentCopyText(attempt)}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : <p className="text-sm text-muted-foreground">No Keystone email attempts are recorded.</p>}
          </section>

          <section aria-labelledby="field-sources-title" className="space-y-2">
            <h3 id="field-sources-title" className="font-medium">Current billing field sources</h3>
            {data.current_field_provenance ? (
              <dl className="grid gap-2 sm:grid-cols-2">
                {(Object.entries(fieldLabels) as [InvoiceIdentityFieldName, string][]).map(([field, label]) => {
                  const provenance = data.current_field_provenance?.[field];
                  if (!provenance) return null;
                  const description = provenance.kind === 'kept_during_edit'
                    ? 'Kept during an earlier billing edit'
                    : provenance.kind === 'explicit_override'
                      ? 'Invoice-specific value'
                      : `Inherited from ${sourceLabel(provenance.source)}`;
                  return (
                    <div key={field} className="rounded border p-2" data-testid={`field-provenance-${field}`}>
                      <dt className="font-medium">{label}</dt>
                      <dd className="text-sm text-muted-foreground">{description}</dd>
                    </div>
                  );
                })}
              </dl>
            ) : <p className="text-sm text-muted-foreground">Current field source details are unavailable.</p>}
          </section>

          <section aria-labelledby="billing-changes-title" className="space-y-2">
            <h3 id="billing-changes-title" className="font-medium">Billing changes</h3>
            {identityChanges.length ? (
              <ol className="space-y-2">
                {identityChanges.map((change) => {
                  const lines = identityChangeLines(change);
                  const isMerge = change.event_type === 'merge' || change.intent === 'invoice_merge';
                  return (
                    <li key={change.id} className="rounded border p-3" data-testid="billing-history-change">
                      <p className="font-medium">{change.actor} · {intentLabel(change.intent)}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(change.created_at)}</p>
                      {isMerge ? (
                        <p className="mt-1 text-sm" data-testid="billing-change-merge-note">
                          This invoice was combined with another. The full decision is listed under Merged invoice history.
                        </p>
                      ) : null}
                      {lines.length ? (
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{lines}</ul>
                      ) : (
                        <p className="mt-2 text-sm">
                          {change.selected
                            ? 'The reviewed billing source changed; printed values did not change.'
                            : 'Billing details were kept as they were during this update.'}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ol>
            ) : <p className="text-sm text-muted-foreground">No billing changes are recorded.</p>}
            {data.unsupported_events?.length ? (
              <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="unsupported-events">
                <p>
                  {data.unsupported_events.length} recorded change{data.unsupported_events.length === 1 ? ' is' : 's are'} of a type this screen cannot display. {data.unsupported_events.length === 1 ? 'It is' : 'They are'} kept, not interpreted:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {data.unsupported_events.map((event) => (
                    <li key={event.id}>{humanize(event.intent)} · {formatDate(event.created_at)} · {event.actor ?? 'unknown actor'}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <section aria-labelledby="description-changes-title" className="space-y-2" data-testid="description-changes">
            <h3 id="description-changes-title" className="font-medium">Line description changes</h3>
            {data.description_changes?.length ? (
              <ol className="space-y-2">
                {data.description_changes.map((change) => (
                  <li key={change.id} className="rounded border p-3" data-testid="description-change">
                    <p className="text-sm">{describeDescriptionChange(change.before, change.after)}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(change.created_at)} · {change.actor ?? 'unknown actor'}
                    </p>
                    <FullDescriptionWording before={change.before} after={change.after} />
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-muted-foreground">No line description changes are recorded.</p>}
          </section>

          {lineage?.has_merge_history || lineageEvents?.length ? (
            <section aria-labelledby="lineage-history-title" className="space-y-2" data-testid="lineage-history">
              <h3 id="lineage-history-title" className="font-medium">Merged invoice history</h3>
              <p className="text-sm text-muted-foreground">
                Every event of this invoice and of each invoice merged into it, labelled with the invoice it was recorded on.
              </p>
              {lineage?.members.length ? (
                <ul className="flex flex-wrap gap-2 text-sm" data-testid="lineage-members">
                  {lineage.members.map((member) => (
                    <li key={member.invoice_id} className="rounded border px-2 py-1">
                      {member.invoice_number ?? member.invoice_id}
                      {member.role === 'current' ? ' (current)' : member.merged_into_invoice_number ? ` → ${member.merged_into_invoice_number}` : ''}
                      {member.merge_origin === 'legacy_pointer' ? ' · older merge, actor and time not recorded' : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              {lineageError ? <p role="alert">Could not load merged history: {lineageError}</p> : null}
              {lineageEvents?.length ? (
                <ol className="space-y-2">
                  {lineageEvents.map((event) => (
                    <li key={event.event_id} className="rounded border p-3" data-testid="lineage-event">
                      <p className="font-medium">{describeLineageEvent(event, numberOf)}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(event.occurred_at)}{event.actor ? ` · ${event.actor}` : ''} · {event.provenance.label}
                        {isOriginalVersion(event) && (event.kind === 'send_attempt' || SEND_EVIDENCE_KINDS.has(event.kind))
                          ? ' · Sent before combining'
                          : ''}
                      </p>
                      {event.kind === 'description_change' ? (
                        <FullDescriptionWording
                          before={lineDescriptionOf((event.details as Record<string, unknown>).before_fields)}
                          after={lineDescriptionOf((event.details as Record<string, unknown>).after_fields)}
                        />
                      ) : null}
                    </li>
                  ))}
                </ol>
              ) : lineageLoading ? <p role="status">Loading merged history…</p> : null}
              {lineageHasMore && onLoadMoreLineage ? (
                <button
                  type="button"
                  className="rounded border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  onClick={onLoadMoreLineage}
                  disabled={lineageLoading}
                  data-testid="lineage-load-more"
                >
                  Load more history
                </button>
              ) : null}
            </section>
          ) : null}

          <p className="text-sm text-muted-foreground" data-testid="issuance-summary">
            {data.first_recorded_send_at
              ? `A send or external-send assertion is recorded from ${formatDate(data.first_recorded_send_at)}.`
              : 'No send or external-send assertion is recorded; this does not establish that the invoice was never sent.'}
          </p>
          {data.billing_details_changed_after_recorded_send ? (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="updated-after-send">
              Billing details or line descriptions changed after a recorded send or external-send assertion. The dated changes above show what changed.
            </p>
          ) : null}
        </>
      ) : !loading && !error ? (
        <p className="text-sm text-muted-foreground">Invoice history is not available yet.</p>
      ) : null}
    </section>
  );
}
